import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../../config';
import './StripeAutopaySetup.css';


// Stripe Card Form Component
const CardPaymentMethodForm = ({ onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      onError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    setProcessing(true);

    const cardElement = elements.getElement(CardElement);
    
    // Create payment method with billing details
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: billingDetails.name || undefined,
        email: billingDetails.email || undefined,
        phone: billingDetails.phone || undefined,
      }
    });

    if (pmError) {
      onError(pmError.message);
      setProcessing(false);
      return;
    }

    // Success - payment method created
    onSuccess(paymentMethod.id, 'card');
    setProcessing(false);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-autopay-form">
      <div className="billing-details-section">
        <h4>Billing Information (Optional)</h4>
        <div className="form-row">
          <input
            type="text"
            placeholder="Cardholder Name"
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({...billingDetails, name: e.target.value})}
            className="form-input"
          />
        </div>
        <div className="form-row">
          <input
            type="email"
            placeholder="Email (Optional)"
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({...billingDetails, email: e.target.value})}
            className="form-input"
          />
        </div>
      </div>

      <div className="card-element-wrapper">
        <label className="form-label">Card Details</label>
        <div className="stripe-card-element">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <div className="security-notice">
        <span className="lock-icon">🔒</span>
        <span>Your card information is securely processed by Stripe. We never store your card details.</span>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="cancel-btn"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="submit-btn"
        >
          {processing ? 'Processing...' : '💳 Save Card for Autopay'}
        </button>
      </div>
    </form>
  );
};

// Bank Account Setup Component
const BankAccountSetup = ({ tenantId, onSuccess, onError, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const initStripe = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/stripe/config`);
        const config = await response.json();
        if (config.publishableKey) {
          setStripePromise(await loadStripe(config.publishableKey));
        }
      } catch (error) {
        console.error('Error loading Stripe:', error);
        onError('Failed to load Stripe. Please refresh the page.');
      }
    };
    initStripe();
  }, [onError]);

  const handleConnectBank = async () => {
    if (!stripePromise || !tenantId) {
      onError('Stripe is not loaded or tenant ID is missing.');
      return;
    }

    setLoading(true);

    try {
      // Create bank session
      const response = await fetch(`${API_BASE_URL}/api/stripe/create-bank-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create bank session');
      }

      if (!data.client_secret) {
        throw new Error('No client secret returned');
      }

      const stripe = await stripePromise;
      
      // Use Financial Connections Sheet
      const sheet = stripe.financialConnectionsSheet({
        clientSecret: data.client_secret,
      });

      const result = await sheet.open();

      if (result.error) {
        onError(result.error.message);
        setLoading(false);
        return;
      }

      if (result.payment_method) {
        // Success - payment method created
        onSuccess(result.payment_method.id, 'us_bank_account');
      } else {
        onError('No payment method returned from bank connection');
      }
    } catch (err) {
      console.error('Error connecting bank:', err);
      onError(err.message || 'Failed to connect bank account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bank-account-setup">
      <div className="setup-info">
        <h4>Connect Bank Account for Autopay</h4>
        <p>Securely connect your bank account using Stripe Financial Connections. Your account details are never stored on our servers.</p>
      </div>

      <div className="bank-benefits">
        <div className="benefit-item">
          <span className="benefit-icon">💰</span>
          <span>Lower fees than credit cards</span>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">🔒</span>
          <span>Bank-level security</span>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">⚡</span>
          <span>Automatic payments</span>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="cancel-btn"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConnectBank}
          disabled={!stripePromise || loading}
          className="submit-btn"
        >
          {loading ? 'Connecting...' : '🏦 Connect Bank Account'}
        </button>
      </div>
    </div>
  );
};

// Main Autopay Setup Component
const StripeAutopaySetup = ({ tenantId, tenantName, tenantEmail, onSuccess, onClose, mode = 'tenant' }) => {
  const [paymentMethodType, setPaymentMethodType] = useState('card'); // 'card' or 'bank'
  const [stripePromise, setStripePromise] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [currentAutopay, setCurrentAutopay] = useState(null);

  useEffect(() => {
    const initStripe = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/stripe/config`);
        const config = await response.json();
        if (config.publishableKey) {
          setStripePromise(await loadStripe(config.publishableKey));
        }
      } catch (error) {
        console.error('Error loading Stripe:', error);
        setError('Failed to load Stripe. Please refresh the page.');
      }
    };
    initStripe();
    loadCurrentAutopay();
  }, [tenantId]);

  const loadCurrentAutopay = async () => {
    if (!tenantId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantId}/autopay`);
      if (response.ok) {
        const data = await response.json();
        setCurrentAutopay(data);
      }
    } catch (err) {
      console.error('Error loading autopay:', err);
    }
  };

  const handlePaymentMethodSuccess = async (paymentMethodId, paymentMethodType) => {
    setProcessing(true);
    setError(null);

    try {
      // Save payment method and set up autopay
      const response = await fetch(`${API_BASE_URL}/api/stripe/save-payment-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          payment_method_id: paymentMethodId,
          payment_method_type: paymentMethodType
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save payment method');
      }

      // Set up recurring payments
      await setupRecurringPayments(paymentMethodId, paymentMethodType);

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      console.error('Error setting up autopay:', err);
      setError(err.message || 'Failed to set up autopay');
      setProcessing(false);
    }
  };

  const setupRecurringPayments = async (paymentMethodId, paymentMethodType) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe/setup-recurring-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          payment_method_id: paymentMethodId,
          payment_method_type: paymentMethodType
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.warn('Recurring payment setup warning:', data.error);
        // Don't fail the whole process if recurring setup fails
      }
    } catch (err) {
      console.error('Error setting up recurring payments:', err);
      // Don't fail the whole process
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate autopay? Future payments will need to be made manually.')) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${tenantId}/autopay`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        if (onSuccess) {
          onSuccess({ deactivated: true });
        }
      } else {
        throw new Error(data.error || 'Failed to deactivate autopay');
      }
    } catch (err) {
      setError(err.message || 'Failed to deactivate autopay');
    } finally {
      setProcessing(false);
    }
  };

  const formatPaymentMethod = (method) => {
    if (!method) return 'None';
    if (method === 'credit' || method === 'card') return '💳 Credit/Debit Card';
    if (method === 'bank') return '🏦 Bank Account';
    return method;
  };

  return (
    <div className="stripe-autopay-setup-overlay" onClick={onClose}>
      <div className="stripe-autopay-setup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="autopay-modal-header">
          <h2>Setup Autopay</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="autopay-modal-body">
          {currentAutopay && currentAutopay.autopay_enabled && (
            <div className="current-autopay-info">
              <div className="info-badge active">
                <span className="badge-icon">✓</span>
                <span>Autopay is currently active</span>
              </div>
              <div className="autopay-details">
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{formatPaymentMethod(currentAutopay.autopay_method)}</span>
                </div>
                {currentAutopay.autopay_method === 'bank' && currentAutopay.bank_name && (
                  <div className="detail-row">
                    <span className="detail-label">Bank:</span>
                    <span className="detail-value">{currentAutopay.bank_name}</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleDeactivate}
                className="deactivate-btn"
                disabled={processing}
              >
                Deactivate Autopay
              </button>
            </div>
          )}

          {(!currentAutopay || !currentAutopay.autopay_enabled) && (
            <>
              <div className="payment-method-selector">
                <label className="selector-label">Choose Payment Method:</label>
                <div className="method-options">
                  <button
                    type="button"
                    className={`method-option ${paymentMethodType === 'card' ? 'active' : ''}`}
                    onClick={() => setPaymentMethodType('card')}
                  >
                    <span className="method-icon">💳</span>
                    <span className="method-name">Credit/Debit Card</span>
                  </button>
                  <button
                    type="button"
                    className={`method-option ${paymentMethodType === 'bank' ? 'active' : ''}`}
                    onClick={() => setPaymentMethodType('bank')}
                  >
                    <span className="method-icon">🏦</span>
                    <span className="method-name">Bank Account</span>
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message">{error}</div>
              )}

              {paymentMethodType === 'card' && stripePromise && (
                <Elements stripe={stripePromise}>
                  <CardPaymentMethodForm
                    onSuccess={handlePaymentMethodSuccess}
                    onError={(err) => setError(err)}
                    onCancel={onClose}
                  />
                </Elements>
              )}

              {paymentMethodType === 'bank' && (
                <BankAccountSetup
                  tenantId={tenantId}
                  onSuccess={handlePaymentMethodSuccess}
                  onError={(err) => setError(err)}
                  onCancel={onClose}
                />
              )}

              {!stripePromise && paymentMethodType === 'card' && (
                <div className="loading-message">Loading Stripe...</div>
              )}
            </>
          )}

          {processing && (
            <div className="processing-overlay">
              <div className="processing-spinner">⟳</div>
              <p>Processing...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeAutopaySetup;

